import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { API_CONFIG } from '../../config';
import { ICONS } from '../../constants';

interface TaxesAndCurrencyPanelProps {
  canEdit: boolean;
}

interface TaxSettings {
  default_currency: string;
  default_vat_rate: string;
  default_profit_tax_rate: string;
}

const CURRENCIES = [
  { code: 'RUB', symbol: '₽', name: 'Российский рубль' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'BYN', symbol: 'Br', name: 'Белорусский рубль' },
];

const TaxesAndCurrencyPanel: React.FC<TaxesAndCurrencyPanelProps> = ({ canEdit }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [settings, setSettings] = useState<TaxSettings>({
    default_currency: 'RUB',
    default_vat_rate: '20',
    default_profit_tax_rate: '20',
  });
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(`${API_CONFIG.API_URL}/rates/defaults`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch settings');
      }

      const data = await response.json();
      setSettings({
        default_currency: data.default_currency || 'RUB',
        default_vat_rate: String(data.default_vat_rate ?? 20),
        default_profit_tax_rate: String(data.default_profit_tax_rate ?? 20),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key: keyof TaxSettings, value: string | number) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
    setSuccess(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Convert strings back to numbers for API
      const payload = {
        default_currency: settings.default_currency,
        default_vat_rate: parseFloat(settings.default_vat_rate) || 0,
        default_profit_tax_rate: parseFloat(settings.default_profit_tax_rate) || 0,
      };

      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(`${API_CONFIG.API_URL}/rates/defaults`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save settings');
      }

      setSuccess(t('rates.taxes.saveSuccess') || 'Настройки сохранены');
      setHasChanges(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
        <span className="ml-3 text-gray-400">{t('rates.common.loading') || 'Загрузка...'}</span>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-white mb-2">
          {t('rates.taxes.title') || 'Налоги и валюты'}
        </h3>
        <p className="text-gray-400 text-sm">
          {t('rates.taxes.description') || 'Финансовые параметры по умолчанию'}
        </p>
      </div>

      {/* Error/Success messages */}
      {error && (
        <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-500/20 border border-green-500/50 rounded-lg text-green-400 text-sm">
          {success}
        </div>
      )}

      {/* Not editable warning */}
      {!canEdit && (
        <div className="mb-4 p-3 bg-yellow-500/20 border border-yellow-500/50 rounded-lg text-yellow-400 text-sm">
          {t('rates.common.noPermission') || 'Недостаточно прав для редактирования'}
        </div>
      )}

      <div className="space-y-6">
        {/* Default Currency */}
        <div className="bg-gray-700/30 rounded-lg p-4">
          <label className="block text-white font-medium mb-3">
            {t('rates.taxes.defaultCurrency') || 'Валюта по умолчанию'}
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {CURRENCIES.map(currency => (
              <button
                key={currency.code}
                onClick={() => handleChange('default_currency', currency.code)}
                disabled={!canEdit}
                className={`
                  p-3 rounded-lg border transition-all text-center
                  ${settings.default_currency === currency.code
                    ? 'bg-indigo-600 border-indigo-600 text-white'
                    : 'bg-gray-700 border-gray-600 text-gray-300 hover:border-gray-500'
                  }
                  disabled:opacity-50 disabled:cursor-not-allowed
                `}
              >
                <div className="text-2xl mb-1">{currency.symbol}</div>
                <div className="text-xs font-medium">{currency.code}</div>
                <div className="text-xs text-gray-400 truncate">{currency.name}</div>
              </button>
            ))}
          </div>
        </div>

        {/* VAT Rate */}
        <div className="bg-gray-700/30 rounded-lg p-4">
          <label className="block text-white font-medium mb-3">
            {t('rates.taxes.vatRate') || 'Ставка НДС'} (%)
          </label>
          <div className="flex items-center space-x-4">
            <input
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={settings.default_vat_rate}
              onChange={(e) => handleChange('default_vat_rate', e.target.value)}
              disabled={!canEdit}
              className="w-32 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-lg
                focus:outline-none focus:border-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <span className="text-gray-400">%</span>
            
            {/* Quick select buttons */}
            <div className="flex space-x-2">
              {[0, 10, 20].map(rate => (
                <button
                  key={rate}
                  onClick={() => handleChange('default_vat_rate', String(rate))}
                  disabled={!canEdit}
                  className={`
                    px-3 py-1.5 text-sm rounded-lg transition-colors
                    ${parseFloat(settings.default_vat_rate) === rate
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-700 text-gray-400 hover:text-white'
                    }
                    disabled:opacity-50 disabled:cursor-not-allowed
                  `}
                >
                  {rate}%
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Profit Tax Rate */}
        <div className="bg-gray-700/30 rounded-lg p-4">
          <label className="block text-white font-medium mb-3">
            {t('rates.taxes.profitTaxRate') || 'Налог на прибыль'} (%)
          </label>
          <div className="flex items-center space-x-4">
            <input
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={settings.default_profit_tax_rate}
              onChange={(e) => handleChange('default_profit_tax_rate', e.target.value)}
              disabled={!canEdit}
              className="w-32 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-lg
                focus:outline-none focus:border-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <span className="text-gray-400">%</span>
            
            {/* Quick select buttons */}
            <div className="flex space-x-2">
              {[0, 6, 13, 20].map(rate => (
                <button
                  key={rate}
                  onClick={() => handleChange('default_profit_tax_rate', String(rate))}
                  disabled={!canEdit}
                  className={`
                    px-3 py-1.5 text-sm rounded-lg transition-colors
                    ${parseFloat(settings.default_profit_tax_rate) === rate
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-700 text-gray-400 hover:text-white'
                    }
                    disabled:opacity-50 disabled:cursor-not-allowed
                  `}
                >
                  {rate}%
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Save button */}
      {canEdit && (
        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className={`
              flex items-center space-x-2 px-6 py-2.5 rounded-lg font-medium transition-all
              ${hasChanges
                ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                : 'bg-gray-700 text-gray-400 cursor-not-allowed'
              }
              disabled:opacity-50
            `}
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>{t('rates.common.loading') || 'Сохранение...'}</span>
              </>
            ) : (
              <>
                <ICONS.Edit className="w-4 h-4" />
                <span>{t('rates.common.save') || 'Сохранить'}</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default TaxesAndCurrencyPanel;
